import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { errorMessage, providerLabel } from "./aiRoutes";

const STATE_STYLE = {
  healthy: "border-emerald-700/50 text-emerald-300 bg-emerald-900/20",
  ready: "border-emerald-700/50 text-emerald-300 bg-emerald-900/20",
  degraded: "border-orange-700/50 text-orange-300 bg-orange-900/20",
  failing: "border-red-700/50 text-red-300 bg-red-900/20",
  missing_credentials: "border-zinc-700 text-zinc-400 bg-zinc-800/40",
  disabled: "border-zinc-700 text-zinc-400 bg-zinc-800/40",
  not_configured: "border-zinc-700 text-zinc-400 bg-zinc-800/40",
};

const STATE_LABEL = {
  healthy: "Healthy",
  ready: "Ready",
  degraded: "Degraded",
  failing: "Failing",
  missing_credentials: "Missing credentials",
  disabled: "Disabled",
  not_configured: "Not configured",
};

const fmtNum = (n) => (typeof n === "number" ? n.toLocaleString() : "0");
const fmtCost = (n) => (typeof n === "number" ? `$${n.toFixed(4)}` : "n/a");
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function Stat({ label, value, hint }) {
  return (
    <div className="bg-zinc-950/60 border border-zinc-800 rounded-lg p-3">
      <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="text-lg font-semibold text-zinc-100">{value}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-0.5">{hint}</div>}
    </div>
  );
}

/**
 * Provider health per route, token usage and estimated cost, recent requests and failures.
 * Read-only. Model switching lives in the route configuration forms.
 */
export default function AIHealthPanel({ settings }) {
  const [health, setHealth] = useState(null);
  const [usage, setUsage] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, u] = await Promise.all([
        api.get("/api/ai/health"),
        api.get("/api/ai/usage", { params: { days, limit: 50 } }),
      ]);
      setHealth(h.data);
      setUsage(u.data);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load AI health"));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load, settings]);

  const failures = (usage?.recent || []).filter((r) => r.status === "error").slice(0, 10);
  const totals = usage?.totals || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-200">Provider health</h3>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-3 py-1.5 rounded-lg border border-zinc-700 hover:bg-zinc-800 text-xs text-zinc-300 flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(health?.providers || []).map((provider) => (
          <div key={provider.provider} className="border border-zinc-800 rounded-lg p-4 bg-zinc-950/40">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-sm text-zinc-100">{provider.label}</div>
              <span className={`text-[11px] px-2 py-0.5 rounded border ${STATE_STYLE[provider.current_status] || STATE_STYLE.not_configured}`}>
                {STATE_LABEL[provider.current_status] || provider.current_status}
              </span>
            </div>
            <div className="text-xs text-zinc-400 space-y-1">
              <div>Calls (24h): {fmtNum(provider.calls_24h)} · Errors: {fmtNum(provider.errors_24h)} · Fallbacks: {fmtNum(provider.fallbacks_24h)}</div>
              <div>Last success: {fmtTime(provider.last_success)}{provider.last_success_model ? ` · ${provider.last_success_model}` : ""}</div>
              {provider.last_error && (
                <div className="text-red-300">
                  Last error ({fmtTime(provider.last_error.at)}): {provider.last_error.message || provider.last_error.code}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <h3 className="text-sm font-semibold text-zinc-200">Route and model health</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(health?.routes || []).map((r) => (
          <div key={r.route} className="border border-zinc-800 rounded-lg p-4 bg-zinc-950/40">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-sm text-zinc-100">{r.label}</div>
              <span className={`text-[11px] px-2 py-0.5 rounded border ${STATE_STYLE[r.state] || STATE_STYLE.not_configured}`}>
                {STATE_LABEL[r.state] || r.state}
              </span>
            </div>
            <div className="text-xs text-zinc-400 space-y-1">
              <div>Model: <span className="text-zinc-200">{providerLabel(settings, r.provider)} · {r.model}</span></div>
              <div>Calls (24h): {fmtNum(r.calls_24h)} · Errors: {fmtNum(r.errors_24h)}</div>
              <div>Last success: {fmtTime(r.last_success)}</div>
              {r.last_error && <div className="text-red-300">Last error ({fmtTime(r.last_error.at)}): {r.last_error.message || r.last_error.code}</div>}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-200">Usage</h3>
        <select
          className="bg-zinc-950 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-200"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value={1}>Last 24 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-9 gap-3">
        <Stat label="Calls" value={fmtNum(totals.calls)} />
        <Stat label="Successful" value={fmtNum(totals.successful_calls)} />
        <Stat label="Failed calls" value={fmtNum(totals.errors)} hint={`${fmtNum(totals.failed_provider_attempts)} provider attempts failed`} />
        <Stat label="Fallbacks used" value={fmtNum(totals.fallbacks)} hint={`${fmtNum(totals.fallback_success_rate)}% succeeded`} />
        <Stat label="Input tokens" value={fmtNum(totals.input_tokens)} />
        <Stat label="Output tokens" value={fmtNum(totals.output_tokens)} />
        <Stat label="Avg latency" value={totals.avg_latency_ms ? `${Math.round(totals.avg_latency_ms)} ms` : "—"} />
        <Stat label="Est. cost" value={fmtCost(totals.estimated_cost_usd)} hint="Estimate from list prices" />
      </div>

      <div className="overflow-x-auto border border-zinc-800 rounded-lg">
        <table className="w-full text-xs text-left">
          <thead className="bg-zinc-950 text-zinc-500">
            <tr>
              <th className="px-3 py-2">Route</th>
              <th className="px-3 py-2">Provider · model</th>
              <th className="px-3 py-2 text-right">Calls</th>
              <th className="px-3 py-2 text-right">Errors</th>
              <th className="px-3 py-2 text-right">Input</th>
              <th className="px-3 py-2 text-right">Output</th>
              <th className="px-3 py-2 text-right">Est. cost</th>
            </tr>
          </thead>
          <tbody>
            {(usage?.by_route || []).length === 0 && (
              <tr><td colSpan={7} className="px-3 py-4 text-center text-zinc-500">No usage in this period.</td></tr>
            )}
            {(usage?.by_route || []).map((row, i) => (
              <tr key={`${row.route}-${row.provider}-${row.model}-${i}`} className="border-t border-zinc-800 text-zinc-300">
                <td className="px-3 py-2">{row.route}</td>
                <td className="px-3 py-2">{providerLabel(settings, row.provider)} · {row.model}</td>
                <td className="px-3 py-2 text-right">{fmtNum(row.calls)}</td>
                <td className="px-3 py-2 text-right">{fmtNum(row.errors)}</td>
                <td className="px-3 py-2 text-right">{fmtNum(row.input_tokens)}</td>
                <td className="px-3 py-2 text-right">{fmtNum(row.output_tokens)}</td>
                <td className="px-3 py-2 text-right">{fmtCost(row.estimated_cost_usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-zinc-200 mb-2">Recent failures</h3>
        {failures.length === 0 ? (
          <p className="text-xs text-zinc-500">No failures in the recent requests.</p>
        ) : (
          <ul className="space-y-2">
            {failures.map((f, i) => (
              <li key={f.id || i} className="text-xs border border-red-900/40 bg-red-900/10 rounded px-3 py-2 text-red-200">
                <span className="text-zinc-400">{fmtTime(f.ts)}</span> · {f.route} · {providerLabel(settings, f.provider)} · {f.model}
                {f.error_code && <span className="ml-2 text-red-400">[{f.error_code}]</span>}
                <div className="text-red-300/90 mt-1 break-words">{f.error}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-zinc-200 mb-2">Recent requests</h3>
        <div className="overflow-x-auto border border-zinc-800 rounded-lg max-h-80">
          <table className="w-full text-xs text-left">
            <thead className="bg-zinc-950 text-zinc-500 sticky top-0">
              <tr>
                <th className="px-3 py-2">Time</th>
                <th className="px-3 py-2">Route</th>
                <th className="px-3 py-2">Feature</th>
                <th className="px-3 py-2">Requested provider</th>
                <th className="px-3 py-2">Provider used · model</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Latency</th>
                <th className="px-3 py-2 text-right">Tokens in / out</th>
                <th className="px-3 py-2">Fallback chain / error</th>
              </tr>
            </thead>
            <tbody>
              {(usage?.recent || []).length === 0 && (
                <tr><td colSpan={9} className="px-3 py-4 text-center text-zinc-500">No requests yet.</td></tr>
              )}
              {(usage?.recent || []).map((r, i) => (
                <tr key={r.id || i} className="border-t border-zinc-800 text-zinc-300">
                  <td className="px-3 py-2 whitespace-nowrap">{fmtTime(r.ts)}</td>
                  <td className="px-3 py-2">{r.route}</td>
                  <td className="px-3 py-2">{r.feature}{r.fallback_used ? " (fallback)" : ""}</td>
                  <td className="px-3 py-2">{providerLabel(settings, r.requested_provider || r.provider)}</td>
                  <td className="px-3 py-2">{providerLabel(settings, r.final_provider || r.provider)} · {r.final_model || r.model}</td>
                  <td className={`px-3 py-2 ${r.status === "error" ? "text-red-400" : "text-emerald-400"}`}>{r.status}</td>
                  <td className="px-3 py-2 text-right">{r.latency_ms ? `${r.latency_ms} ms` : "—"}</td>
                  <td className="px-3 py-2 text-right">{r.input_tokens ?? "—"} / {r.output_tokens ?? "—"}</td>
                  <td className="px-3 py-2">{(r.provider_chain || [r.provider]).join(" → ")}{r.error && <div className="text-red-300 break-words">{r.error}</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
